import React, { useState } from "react";
import {
  Patient,
  CreatePatientPayload,
  UpdatePatientPayload,
} from "types/patient";
import { patientService } from "services/patientService";

interface PatientFormProps {
  onClose: () => void;
  onSuccess: () => void;
  patient?: Patient | null;
}

const PatientForm: React.FC<PatientFormProps> = ({
  onClose,
  onSuccess,
  patient = null,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: patient?.name || "",
    email: patient?.email || "",
    password: "",
    phone: patient?.patient_profile?.phone || "",
    address: patient?.patient_profile?.address || "",
    birth_date: patient?.patient_profile?.birth_date?.split("T")[0] || "",
    gender: patient?.patient_profile?.gender || "other",
    health_insurance_company:
      patient?.patient_profile?.health_insurance_company || "",
    health_insurance_plan:
      patient?.patient_profile?.health_insurance_plan || "",
    health_insurance_id: patient?.patient_profile?.health_insurance_id || "",
    occupation: patient?.patient_profile?.occupation || "",
    marital_status: patient?.patient_profile?.marital_status || "soltero",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (patient) {
        const updateData: UpdatePatientPayload = {
          ...formData,
          // Only include password if it was changed
          ...(formData.password ? { password: formData.password } : {}),
        };
        const response = await patientService.updatePatient(
          patient.id,
          updateData
        );
        if (response.success) {
          onSuccess();
        } else {
          setError(response.message);
        }
      } else {
        const response = await patientService.createPatient(
          formData as CreatePatientPayload
        );
        if (response.success) {
          onSuccess();
        } else {
          setError(response.message);
        }
      }
    } catch (err) {
      setError("Failed to save patient");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="bg-black fixed inset-0 z-50 flex items-center justify-center bg-opacity-50">
      <div className="max-h-[90vh] w-full max-w-4xl rounded-lg bg-white shadow-xl dark:bg-navy-700">
        <div className="p-6">
          <h2 className="mb-4 text-xl font-bold text-navy-700 dark:text-white">
            {patient ? "Editar Paciente" : "Nuevo Paciente"}
          </h2>

          {error && (
            <div className="mb-4 rounded-lg bg-red-100 p-4 text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="max-h-[calc(90vh-200px)] overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                    Nombre
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full rounded-lg border border-gray-300 p-2.5 text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:focus:border-brand-400"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full rounded-lg border border-gray-300 p-2.5 text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:focus:border-brand-400"
                  />
                </div>

                {!patient && (
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                      Contraseña
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required={!patient}
                      className="w-full rounded-lg border border-gray-300 p-2.5 text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:focus:border-brand-400"
                    />
                  </div>
                )}

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full rounded-lg border border-gray-300 p-2.5 text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:focus:border-brand-400"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                    Fecha de Nacimiento
                  </label>
                  <input
                    type="date"
                    name="birth_date"
                    value={formData.birth_date}
                    onChange={handleChange}
                    required
                    className="w-full rounded-lg border border-gray-300 p-2.5 text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:focus:border-brand-400"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                    Género
                  </label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    required
                    className="w-full rounded-lg border border-gray-300 p-2.5 text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:focus:border-brand-400"
                  >
                    <option value="male">Masculino</option>
                    <option value="female">Femenino</option>
                    <option value="other">Otro</option>
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                    Compañía de Seguro
                  </label>
                  <input
                    type="text"
                    name="health_insurance_company"
                    value={formData.health_insurance_company}
                    onChange={handleChange}
                    required
                    className="w-full rounded-lg border border-gray-300 p-2.5 text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:focus:border-brand-400"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                    Plan de Seguro
                  </label>
                  <input
                    type="text"
                    name="health_insurance_plan"
                    value={formData.health_insurance_plan}
                    onChange={handleChange}
                    required
                    className="w-full rounded-lg border border-gray-300 p-2.5 text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:focus:border-brand-400"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                    ID de Seguro
                  </label>
                  <input
                    type="text"
                    name="health_insurance_id"
                    value={formData.health_insurance_id}
                    onChange={handleChange}
                    required
                    className="w-full rounded-lg border border-gray-300 p-2.5 text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:focus:border-brand-400"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                    Ocupación
                  </label>
                  <input
                    type="text"
                    name="occupation"
                    value={formData.occupation}
                    onChange={handleChange}
                    required
                    className="w-full rounded-lg border border-gray-300 p-2.5 text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:focus:border-brand-400"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                    Estado Civil
                  </label>
                  <select
                    name="marital_status"
                    value={formData.marital_status}
                    onChange={handleChange}
                    required
                    className="w-full rounded-lg border border-gray-300 p-2.5 text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:focus:border-brand-400"
                  >
                    <option value="soltero">Soltero</option>
                    <option value="casado">Casado</option>
                    <option value="divorciado">Divorciado</option>
                    <option value="union convivencial">
                      Union Convivencial
                    </option>
                    <option value="viudo">Viudo</option>
                    <option value="separado">Separado</option>
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                    Dirección
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                    className="w-full rounded-lg border border-gray-300 p-2.5 text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:focus:border-brand-400"
                  />
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-4 border-t border-gray-200 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg px-4 py-2 text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-navy-600"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="rounded-lg bg-brand-500 px-4 py-2 text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PatientForm;
